import { useState } from 'react';
import { authAxios } from '../../contexts/AuthContext';
import { KeyRound, Eye, EyeOff, Shield, AlertTriangle, Trash2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { AdminHeader } from '../../components/AdminHeader';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminSettings() {
  const [pwForm, setPwForm] = useState({ current: '', new_pw: '', confirm: '' });
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  // Factory reset
  const [resetOpen, setResetOpen] = useState(false);
  const [resetPw, setResetPw] = useState('');
  const [resetConfirm, setResetConfirm] = useState('');
  const [resetting, setResetting] = useState(false);

  const handleChangePassword = async () => {
    if (!pwForm.current || !pwForm.new_pw) { toast.error('Fill all fields'); return; }
    if (pwForm.new_pw !== pwForm.confirm) { toast.error('Passwords do not match'); return; }
    setLoading(true);
    try {
      await authAxios.post(`${API}/auth/change-password`, { current_password: pwForm.current, new_password: pwForm.new_pw });
      toast.success('Password changed successfully!');
      setPwForm({ current: '', new_pw: '', confirm: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    }
    setLoading(false);
  };

  const handleFactoryReset = async () => {
    if (!resetPw || resetConfirm !== 'RESET') { toast.error('Type RESET and enter your password'); return; }
    setResetting(true);
    try {
      await authAxios.post(`${API}/admin/factory-reset`, { password: resetPw, confirm: resetConfirm });
      toast.success('All data wiped. Fresh start ready.');
      setResetOpen(false); setResetPw(''); setResetConfirm('');
      setTimeout(() => window.location.reload(), 800);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reset failed');
    }
    setResetting(false);
  };

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      <AdminHeader />

      <div className="max-w-md mx-auto px-4 py-5 sm:py-8">
        <h1 className="text-2xl font-bold text-[#1C1C1E] mb-5">Settings</h1>

        {/* Change Password */}
        <div className="bg-white rounded-2xl shadow-sm p-5">
          <div className="flex items-center gap-2 mb-4">
            <KeyRound className="w-4 h-4 text-[#8E8E93]" strokeWidth={1.5} />
            <h2 className="text-[15px] font-semibold text-[#1C1C1E]">Change Admin Password</h2>
          </div>

          <div className="space-y-3">
            <div>
              <Label className="text-[11px] font-semibold uppercase tracking-wider text-[#8E8E93] mb-1.5 block">Current Password</Label>
              <Input data-testid="admin-current-pw" type="password" className="h-12 bg-[#F2F2F7] border-0 rounded-2xl" value={pwForm.current} onChange={(e) => setPwForm(f => ({ ...f, current: e.target.value }))} placeholder="Current password" />
            </div>
            <div>
              <Label className="text-[11px] font-semibold uppercase tracking-wider text-[#8E8E93] mb-1.5 block">New Password</Label>
              <div className="relative">
                <Input data-testid="admin-new-pw" type={showPw ? 'text' : 'password'} className="h-12 bg-[#F2F2F7] border-0 rounded-2xl pr-12" value={pwForm.new_pw} onChange={(e) => setPwForm(f => ({ ...f, new_pw: e.target.value }))} placeholder="New password" />
                <button type="button" className="absolute right-4 top-1/2 -translate-y-1/2 text-[#8E8E93]" onClick={() => setShowPw(!showPw)}>
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <Label className="text-[11px] font-semibold uppercase tracking-wider text-[#8E8E93] mb-1.5 block">Confirm Password</Label>
              <Input data-testid="admin-confirm-pw" type="password" className="h-12 bg-[#F2F2F7] border-0 rounded-2xl" value={pwForm.confirm} onChange={(e) => setPwForm(f => ({ ...f, confirm: e.target.value }))} placeholder="Confirm password" />
            </div>
            <Button data-testid="admin-save-pw-btn" className="w-full h-12 bg-[#1C1C1E] text-white hover:bg-[#2C2C2E] rounded-full text-sm font-semibold mt-2" onClick={handleChangePassword} disabled={loading}>
              {loading ? 'Changing...' : 'Update Password'}
            </Button>
          </div>
        </div>

        {/* Security Info */}
        <div className="bg-white rounded-2xl shadow-sm p-5 mt-4">
          <div className="flex items-center gap-2 mb-3">
            <Shield className="w-4 h-4 text-[#8E8E93]" strokeWidth={1.5} />
            <h2 className="text-[15px] font-semibold text-[#1C1C1E]">Security</h2>
          </div>
          <div className="space-y-2 text-[13px] text-[#8E8E93]">
            <p>All login activity is tracked with IP and location.</p>
            <p>Employee passwords can be changed from the Accounts tab.</p>
            <p>All data changes are logged in the Audit trail.</p>
          </div>
        </div>

        {/* DANGER ZONE */}
        <div className="bg-white border border-red-100 rounded-2xl shadow-sm p-5 mt-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-red-600" strokeWidth={1.5} />
            <h2 className="text-[15px] font-semibold text-red-700">Danger Zone</h2>
          </div>
          <p className="text-[13px] text-[#8E8E93] mb-3">
            Wipe the entire database — all clients, orders, measurements, employee accounts, login history, and audit logs. Your admin account stays. <b>This cannot be undone.</b>
          </p>
          <Button data-testid="factory-reset-open-btn" variant="outline" className="w-full h-11 border-red-200 text-red-700 hover:bg-red-50 rounded-full text-sm font-semibold"
            onClick={() => { setResetOpen(true); setResetPw(''); setResetConfirm(''); }}>
            <Trash2 className="w-4 h-4 mr-1.5" strokeWidth={1.5} />
            Factory Reset
          </Button>
        </div>
      </div>

      {/* Factory Reset Confirmation Dialog */}
      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent className="bg-white max-w-[92vw] sm:max-w-md rounded-2xl">
          <DialogHeader>
            <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mb-2">
              <AlertTriangle className="w-5 h-5 text-red-600" strokeWidth={1.5} />
            </div>
            <DialogTitle className="text-xl font-bold text-red-700">Permanently wipe all data?</DialogTitle>
            <DialogDescription className="text-[13px] text-[#8E8E93]">
              Every client, order, measurement, employee, audit log, and login record will be permanently deleted. Only your admin account will remain. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label className="text-[11px] font-semibold uppercase tracking-wider text-[#8E8E93] mb-1.5 block">Admin Password</Label>
              <Input data-testid="reset-pw-input" type="password" className="h-12 bg-[#F2F2F7] border-0 rounded-2xl" value={resetPw} onChange={(e) => setResetPw(e.target.value)} placeholder="Re-enter your password" />
            </div>
            <div>
              <Label className="text-[11px] font-semibold uppercase tracking-wider text-[#8E8E93] mb-1.5 block">Type RESET to confirm</Label>
              <Input data-testid="reset-confirm-input" className="h-12 bg-[#F2F2F7] border-0 rounded-2xl uppercase" value={resetConfirm} onChange={(e) => setResetConfirm(e.target.value.toUpperCase())} placeholder="RESET" />
            </div>
            <div className="flex gap-3 pt-2">
              <Button variant="outline" className="flex-1 h-11 border-[#E5E5EA] rounded-full text-sm" onClick={() => setResetOpen(false)}>Cancel</Button>
              <Button data-testid="reset-confirm-btn" className="flex-1 h-11 bg-red-600 hover:bg-red-700 text-white rounded-full text-sm font-semibold"
                onClick={handleFactoryReset} disabled={resetting || resetConfirm !== 'RESET' || !resetPw}>
                {resetting ? 'Wiping...' : 'Wipe Everything'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
